import React, { useRef, useEffect, useState, memo } from 'react';
import * as d3 from 'd3';

const RadarChart = memo(({ data, labels }) => {
    const svgRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        // 초기 마운트 시 한 번만 차트 렌더링
        if (svgRef.current) {
            const boundingRect = svgRef.current.parentElement.getBoundingClientRect();
            setDimensions({
                width: boundingRect.width,
                height: boundingRect.width, // height can be the same as width for a square chart
            });
        }
    }, []); // 빈 의존성 배열로 한 번만 실행


    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;

        const { width, height } = dimensions;
        const maxValue = 5; // 최대 점수
        const radius = Math.min(width / 2, height / 2) - 50; // Add padding to avoid clipping
        const angleSlice = Math.PI * 2 / labels.length; // 각도를 5각형에 맞추기
        const labelFactor = 1.08; // 라벨과 그래프 사이의 거리 비율
        const duration = 1000; // 애니메이션 지속 시간 (밀리초)

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // 이전 렌더링 제거

        svg.attr('viewBox', `0 0 ${width} ${height}`)
            .attr('width', '100%')
            .attr('height', '100%');

        const g = svg.append('g')
            .attr('transform', `translate(${width / 2}, ${height / 2})`);

        // 그리드 선을 3점 단위로 그리기, 마지막 선은 5점으로 굵게
        const levels = [3, maxValue]; // 3점과 5점 (마지막 선)

        // Draw the grid (Polygon shape)
        g.selectAll('.grid-polygon')
            .data(levels)
            .enter()
            .append('polygon')
            .attr('class', 'grid-polygon')
            .attr('points', d => {
                return labels.map((label, i) => {
                    const x = radius * d / maxValue * Math.cos(angleSlice * i - Math.PI / 2);
                    const y = radius * d / maxValue * Math.sin(angleSlice * i - Math.PI / 2);
                    return [x, y].join(',');
                }).join(' ');
            })
            .style('fill', 'none')
            .style('stroke', d => d === maxValue ? 'black' : '#CDCDCD')
            .style('stroke-width', d => d === maxValue ? 2 : 1); // 마지막 선을 굵게 설정

        // Draw the axes
        const axisGrid = g.selectAll('.axis')
            .data(labels)
            .enter()
            .append('g')
            .attr('class', 'axis');

        axisGrid.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', (d, i) => radius * Math.cos(angleSlice * i - Math.PI / 2))
            .attr('y2', (d, i) => radius * Math.sin(angleSlice * i - Math.PI / 2))
            .style('stroke', '#CDCDCD')
            .style('stroke-width', 1);

        axisGrid.append('text')
            .attr('x', (d, i) => (radius * labelFactor) * Math.cos(angleSlice * i - Math.PI / 2))
            .attr('y', (d, i) => (radius * labelFactor) * Math.sin(angleSlice * i - Math.PI / 2))
            .attr('text-anchor', d => {
                if (Math.cos(angleSlice * labels.indexOf(d) - Math.PI / 2) > 0.1) {
                    return 'start';
                } else if (Math.cos(angleSlice * labels.indexOf(d) - Math.PI / 2) < -0.1) {
                    return 'end';
                } else {
                    return 'middle';
                }
            })
            .attr('dy', d => {
                const sin = Math.sin(angleSlice * labels.indexOf(d) - Math.PI / 2);
                if (sin > 0.1) {
                    return '0.75em';  // 위로 향하는 경우
                } else if (sin < -0.1) {
                    return '-0.75em'; // 아래로 향하는 경우
                } else {
                    return '0.35em';  // 중앙에 위치한 경우
                }
            })
            .style('font-size', '17px') // 글씨 크기 조정
            .style('font-weight','700')
            .attr('fill', 'black')
            .text(d => d);

        // Draw the data line with animation from the center
        const radarLine = d3.lineRadial()
            .radius((d, i) => radius * d / maxValue)
            .angle((d, i) => i * angleSlice)
            .curve(d3.curveLinearClosed); // 데이터 라인을 이어서 그리기

        const radarPath = g.append('path')
            .datum(data.map(() => 0))  // Start with zero radius (center)
            .attr('d', radarLine)
            .style('fill', 'orange')
            .style('fill-opacity', 0.3)
            .style('stroke', 'orange')
            .style('stroke-width', 2)
            .transition()  // Add transition for animation
            .duration(duration)
            .attrTween('d', function () {
                const interpolator = d3.interpolateArray(data.map(() => 0), data);
                return t => radarLine(interpolator(t));
            });

        // Draw circles on data points
        g.selectAll('.radarCircle')
            .data(data)
            .enter()
            .append('circle')
            .attr('cx', (d, i) => radius * 0 * Math.cos(angleSlice * i - Math.PI / 2))
            .attr('cy', (d, i) => radius * 0 * Math.sin(angleSlice * i - Math.PI / 2))
            .attr('r', 4)
            .style('fill', 'orange')
            .style('fill-opacity', 0.8)
            .transition()  // Add transition for animation
            .duration(duration)
            .attr('cx', (d, i) => radius * d / maxValue * Math.cos(angleSlice * i - Math.PI / 2))
            .attr('cy', (d, i) => radius * d / maxValue * Math.sin(angleSlice * i - Math.PI / 2));

        // Draw lines from center to each point
        g.selectAll('.line-to-point')
            .data(data)
            .enter()
            .append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', (d, i) => 0)  // Start from center
            .attr('y2', (d, i) => 0)  // Start from center
            .style('stroke', 'orange')
            .style('stroke-width', 1)
            .transition()  // Add transition for animation
            .duration(duration)
            .attr('x2', (d, i) => radius * d / maxValue * Math.cos(angleSlice * i - Math.PI / 2))
            .attr('y2', (d, i) => radius * d / maxValue * Math.sin(angleSlice * i - Math.PI / 2));

    }, [dimensions,data]);

    return <svg ref={svgRef}></svg>;
});

const ProductRadar2 = (props) => {
    const data = [1, 2, 2, 4, 2];
    const labels = ["단맛", "상큼함", "바디감", "타격감", "멘솔"];

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <RadarChart data={props.data} labels={labels} />
        </div>
    );
};

export default ProductRadar2;
